/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Grid, Tooltip, IconButton, Button, Grid2, Box, InputAdornment, Typography, Chip } from '@mui/material';
import deleteBtn from '../../../assets/SVG/trash.svg';
import editBtn from '../../../assets/SVG/PencilSimple.svg';
import styles from './DrugsHome.module.css';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { ColumnConfig, GroupConfig, TableRowData } from '../../../types';
import { useLoader } from '../../loaderProvider/loader';
import AddDrugs from './AddDrugs';
import EditableTable from '../../customTableComponent';
import { deletePatientDrugs, getPatientDrugs, Prescription } from './DrugsHomeHelper';
import { ConfirmationModal } from '../../confirmationModal';
import { useAlert } from '../../alertProvider';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import { usePatientContext } from '../../SubPatinetProvider';
import {ReactComponent as PrinterIcon} from '../../../assets/SVG/printer (1).svg';
import { useVerboseLog } from '../../../utils/debug';
import { usePrint } from '../../PrintProvider';
import { TreatmentPlanTypePermission } from '../../../pages/TreatmentPlanPage/treatmentPlan.helper';
import ReusableReportComponent from '../../printReports/ReusableReportComponent';
import DrugListReport from './DrugListReport';
import RemarkPopover from '../../Billing/common/RemarkPopover';
import { ReactComponent as SearchIcon } from '../../../assets/SVG/Search-Icon.svg';
import { formatDate } from '../../../utils/dateUtils';

interface DrugsProps {
  permissionObj: TreatmentPlanTypePermission | undefined;
}


const DrugsHome: React.FC<DrugsProps> = ({ permissionObj }) => {

  const { t: tGeneral } = useTranslation('general');
  const { t: tTP } = useTranslation('treatmentPlan_config');

  const columns: ColumnConfig[] = [
    { field: 'drugName', headerName: tGeneral('drugName'), type: 'text', editable: false },
    { field: 'genericName', headerName: tGeneral('genericName'), type: 'text', editable: false },
    { field: 'strengthDisplay', headerName: `${tGeneral('strength')} / ${tGeneral('strengthType')}`, type: 'text', editable: false },
    { field: 'route', headerName: tGeneral('route'), type: 'text', editable: false },
    { field: 'frequency', headerName: tGeneral('frequency'), type: 'text', editable: false },
    { field: 'instructions', headerName: tGeneral('instructions'), type: 'text', editable: false },
    { field: 'days', headerName: tGeneral('days'), type: 'text', editable: false },
    { field: 'qty', headerName: tGeneral('qty'), type: 'text', editable: false },
    { field: 'remaining_quantity', headerName: tGeneral('remainingQty'), type: 'text', editable: false },
    { field: 'UOM', headerName: tGeneral('UOM'), type: 'text', editable: false },
    { field: 'notes', headerName: tGeneral('notes'), type: 'text', editable: false },
    { field: 'doctor', headerName: tGeneral('doctor'), type: 'text', editable: false },
    { field: 'date', headerName: tGeneral('date'), type: 'text', editable: false },
    { field: 'action', headerName: tGeneral('action'), type: 'text', editable: false },
  ];

  const groupConfig: GroupConfig[] = [
    { fields: ['drugName'], type: 'column' },
    { fields: ['genericName'], type: 'column' },
    { fields: ['strengthDisplay'], type: 'column' },
    // { fields: ['strengthType'], type: 'column' },
    { fields: ['route'], type: 'column' },
    { fields: ['frequency'], type: 'column' },
    { fields: ['instructions'], type: 'column' },
    { fields: ['days'], type: 'column' },
    { fields: ['qty'], type: 'column' },
    { fields: ['remaining_quantity'], type: 'column' },
    { fields: ['UOM'], type: 'column' },
    { fields: ['notes'], type: 'column' },
    { fields: ['doctor'], type: 'column' },
    { fields: ['date'], type: 'column' },
    { fields: ['action'], type: 'column' },
  ];


  const { patientId } = usePatientContext();
  const { TabOption, setTabOption } = usePrint();
  const verboseLog = useVerboseLog();

  const [data, setData] = useState<TableRowData[]>([]);
  const [dataSize, setDataSize] = useState<number>();
  const [showAddDiagnosis, setShowAddDiagnosis] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedData, setSelectedData] = useState<any | null>(null);
  const [selectedDrugId, setSelectedDrugId] = useState<number | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false); 
  const { addAlert } = useAlert();
  const [isDrugDeleted, setIsDrugDeleted] = useState(false);
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const [reportData, setReportData] = useState<TableRowData[]>([]);

  // Generic popover state for Instructions and Notes
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverText, setPopoverText] = useState<string>('');
  const [popoverTitle, setPopoverTitle] = useState<string>('');

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, text: string, title: string) => {
    event.stopPropagation();
    setPopoverAnchorEl(event.currentTarget);
    setPopoverText(text);
    setPopoverTitle(title);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
    setPopoverText('');
    setPopoverTitle('');
  };

  const hasAccess = (type: string) => {
    if (!permissionObj || !permissionObj.permissions) return false;
    return permissionObj.permissions.some((perm) => perm.access === type);
  };

  
  const canAdd = hasAccess('add');
  const canPrint = hasAccess('print');
  const canEditOrView = hasAccess('edit') || hasAccess('view');
  const canEdit = hasAccess('edit');
 
  const handleDelete = (id: number) => {
    setSelectedDrugId(id);
    setOpenConfirmDialog(true);
    // Implement delete logic here
  };


  const handleEdit = (rowData: Prescription) => {
    setSelectedData(rowData);
    setShowAddDiagnosis(true);
    // Implement edit logic here
  };


  const handleSubmit =()=>{
    showLoader();
    if (selectedDrugId) {
      deletePatientDrugs(`${selectedDrugId.toString()}/`).then((res)=>{
        addAlert(tTP('drugDeletedSuccessfully'), 'success');
        setIsDrugDeleted(true);
      })
        .catch((error) => {
          console.error('Error deleting selected Drug', error);
          addAlert(tTP('errorDeletingDrug'), 'error');
          
        })
        .finally(() => {
          hideLoader();
        });
    }
  };

  useEffect(() => {
    showLoader();
    getPatientDrugs(`?patient=${patientId}`)
      .then((res) => { 
        setReportData(res.objects);
        const tableData: TableRowData[] = res.objects.map((item) => {
          const createdOnDate = item.created_on ? new Date(item.created_on) : null;
          const isDisabled = createdOnDate
            ? (new Date().getTime() - createdOnDate.getTime()) / (1000 * 60 * 60) > 24
            : false;
        
          return {
            id: item.id,
            drugNameText: item.drug.name_label,
            drugName: (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <span>{item.drug.name_label}</span>
                {item.remaining_quantity === 0 && (
                  <Chip
                    label={tGeneral('dispensed')}
                    size="small"
                    sx={{
                      backgroundColor: '#E8F5E9',
                      color: '#2E7D32',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      width: 'fit-content',
                    }}
                  />
                )}
              </Box>
            ),
            genericName:
              (item.drug.molecule_label || item.drug.molecule).length > 13 ? (
                <Tooltip title={ convertUnderscoreToTitle(item.drug.molecule_label || item.drug.molecule)}>
                  <span
                    style={{
                      cursor: 'pointer',
                      display: 'inline-block',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    { convertUnderscoreToTitle((item.drug.molecule_label || item.drug.molecule).substring(0, 13))}...
                  </span>
                </Tooltip>
              ) : (
                convertUnderscoreToTitle(item.drug.molecule_label || item.drug.molecule)
              ),
            strengthDisplay: `${item.drug.strength ? item.drug.strength : '-'} / ${item.drug.strength_type_label ?? item.drug.strength_type ?? '-'}`,
            UOM: item.drug.base_uom_label ?? item.drug.base_uom_code ?? '-',
            route: item.drug_route_code_label,
            frequency: item.frequency,
            instructions: item.instructions_label
              ? item.instructions_label.length > 30
                ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Tooltip title={tGeneral('Instructions')} arrow>
                      <IconButton
                        size="small"
                        sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                        aria-label={tGeneral('Instructions')}
                        onClick={(e: React.MouseEvent<HTMLElement>) => handleOpenPopover(e, item.instructions_label!, tGeneral('instructions'))}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography>{item.instructions_label.slice(0, 30)}...</Typography>
                  </Box>
                )
                : (
                  <span>{item.instructions_label}</span>
                )
              : '-',
            days: item.no_of_days,
            qty: item.quantity,
            remaining_quantity: item.remaining_quantity,
            action: (
              canEdit ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                  {/* Edit Button */}
                  <Tooltip title={isDisabled ? tTP('cannotEditPast24Hours') : tGeneral('Text_EditButton')}>
                    <span>
                      <IconButton
                        onClick={() => !isDisabled && handleEdit(item)}
                        disabled={isDisabled}
                        style={{
                          color: isDisabled ? '#B0B0B0' : '#E17C64',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                        size="small"
                      >
                        <img src={editBtn} alt="edit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/* Delete Button */}
                  <Tooltip title={isDisabled ? tTP('cannotDeletePast24Hours') : tGeneral('Delete')}>
                    <span>
                      <IconButton
                        onClick={() => !isDisabled && handleDelete(item.id)}
                        disabled={isDisabled}
                        style={{
                          color: isDisabled ? '#B0B0B0' : '#FF0000',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                        size="small"
                      >
                        <img src={deleteBtn} alt="delete" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              ) : null
            ),
            doctor: item.user_full_name,
            date: formatDate(item.created_on) || '-',
            notes: item.notes
              ? item.notes.length > 10
                ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Tooltip title={tGeneral('Notes')} arrow>
                      <IconButton
                        size="small"
                        sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                        aria-label={tGeneral('Notes')}
                        onClick={(e: React.MouseEvent<HTMLElement>) => handleOpenPopover(e, item.notes!, tGeneral('notes'))}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography>{item.notes.slice(0, 10)}...</Typography>
                  </Box>
                )
                : (
                  <span>{item.notes}</span>
                )
              : '-',
          };
        });
        
        setDataSize(res.meta.total_count);
        setData(tableData);
        
      })
      .catch((error) => {
        console.error('Error fetching patient diagnosis:', error);
      })
      .finally(() => {
        hideLoader();
        setIsDrugDeleted(false); 
      });
  }, [patientId, isDrugDeleted, showAddDiagnosis, showLoader, hideLoader,canEdit, tTP, tGeneral]);


  const filteredData = useMemo(
    () => data.filter(
      (row) =>
        row.drugNameText?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [searchTerm, data]
  );

  useEffect(() => { 
    setDataSize(filteredData.length); // total filtered items 
  }, [filteredData]);


  const handleOnChange = (id: number, field: string, value: string) => {

  };
  


  const handleAddClick = () => {
    setSelectedData(null);
    setShowAddDiagnosis(true);
  };

  const handleBackClick = () => {
    setShowAddDiagnosis(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClose = () => {
    setShowPrint(false);
  };
  return (
    <div className={`${styles.home} mt-4 p-3`} style={{ display: 'flex', flexDirection: 'column' }}>
      {showAddDiagnosis ? (
        <AddDrugs onBackClick={handleBackClick} data={selectedData} />
      ) : (
        <>
          <Grid2 container spacing={2} my={1} mx={1} alignItems="center">
            {canEditOrView && (
              <Grid2 size="grow">
                <p style={{ fontWeight: 600, fontFamily: 'Nunito', fontSize: '18px', margin: 0 }}>{tTP('listOfPrescribedDrugs')}</p>
              </Grid2>
            )}
            {canEditOrView && (
              <Grid2 size={3}>
                <Tooltip
                  title={data.length === 0 ? tTP('searchDrugsDisabledNoData') : ''}
                  arrow placement="bottom">
                  <Box
                    style={{ cursor: data.length === 0 ? 'not-allowed' : 'default' }}
                    tabIndex={data.length === 0 ? 0 : undefined}
                    aria-disabled={data.length === 0 ? true : undefined}
                    aria-label={data.length === 0 ? tTP('searchDrugsDisabledNoData') : undefined}
                  >
                    <TextField
                      id="outlined-basic"
                      placeholder={tTP('searchMedicinePlaceholder')}
                      label={tTP('searchMedicinePlaceholder')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={data.length === 0}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          )
                        },
                        inputLabel: {
                          shrink: searchFocused || searchTerm.length > 0,
                        },
                      }}
                      sx={{
                        pointerEvents: data.length === 0 ? 'none' : 'auto',
                        '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                          transform: 'translate(50px, 10px) scale(1)',
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid2>
            )}
            {canAdd && (
              <Grid2 size="auto">
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                >
                  {tGeneral('Add')}
                </Button>
              </Grid2>
            )}
            {canPrint && (
              <Grid2 size="auto">
                <Tooltip title="Print Report">
                  <IconButton onClick={() => {
                    setShowPrint(true);
                    const newTabOption = {
                      ...TabOption,
                      tabName: 'treatment-plan',
                      subOption: 'drug_list',
                    };
                    setTabOption(newTabOption);
                    verboseLog('Print button clicked for Drug List', newTabOption);
                  }}>
                    <PrinterIcon style={{ width: '24px', height: '24px' }} />
                  </IconButton>
                </Tooltip>
              </Grid2>
            )}
          </Grid2>
          <ReusableReportComponent
            open={showPrint}
            onClose={handleClose}
            reportTitle={tTP('DrugList_Report')}
          >
            <DrugListReport DrugListData={reportData} />
          </ReusableReportComponent>
          <div className="mt-4" style={{ flexGrow: 1 }}>
            {canEditOrView && (
              <EditableTable
                columns={columns}
                data={filteredData}
                actionType="none"
                onChange={handleOnChange}
                groupConfig={groupConfig}
                dataSize={dataSize}
                noDataText={tTP('noDrugsPrescribed')}
              />
            )}
          </div>
          <ConfirmationModal
            open={openConfirmDialog}
            handleClose={() => { setOpenConfirmDialog(false); }}
            onConfirm={() => {
              handleSubmit();
            }}
            type="save"
            title={tTP('deleteDrugTitle')}
            contentMessage={tTP('confirmDeleteDrug')}
          />
          <RemarkPopover
            anchorEl={popoverAnchorEl}
            content={popoverText}
            open={Boolean(popoverAnchorEl)}
            onClose={handleClosePopover}
            title={popoverTitle}
          />
        </>
      )}
    </div>
  );
};

export default DrugsHome;
